import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthBridge } from '@crewdle/auth-bridge';
import { User } from 'firebase/auth';
import { FirebaseService } from './services/firebase/firebase.service';
import { AuthBridgeService, shellNativeSignIn } from './services/auth-bridge.service';
import { environment } from 'src/environments/environment';
import { PartnerDomainsService } from './services/partner-domains/partner-domains.service';
import { HireIntentService } from './services/hire-intent/hire-intent.service';
import { TeamsService } from './chat/core/services/teams.service';
import { RedirectService } from './services/redirect/redirect.service';
import { needsNativeOnboarding } from './native/onboarding/native-onboarding.gate';
import { forgetReturnUrl, isLoginCallback, rememberReturnUrl, takeReturnUrl } from './utils/login-return-url';

const tabStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export type AuthPipeGenerator = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => any;

// Anonymous users (created for guest access on the room/:id/join page) are
// treated as "not logged in" by route guards, so they're redirected to /login
// when they hit a protected page and never bounced off /login as if they were
// already authenticated.
export const loggedIn: any = (user: User) => !!user && !user.isAnonymous;

const retrieveUser = (firebase: FirebaseService): Promise<User> => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 3000);
    const sub = firebase.auth.onAuthStateChanged((user) => {
      clearTimeout(timeout);
      sub();
      resolve(user);
    }, () => {
      clearTimeout(timeout);
      resolve(null);
    });
  });
};

// Refresh from Firebase so a verification done in another tab is picked up,
// then bounce unverified users out — they need to click the email link before
// they can use the app.
const isEmailVerified = async (user: User): Promise<boolean> => {
  if (user.emailVerified) return true;
  // Federated IdPs (Entra/Microsoft, Google, SAML/OIDC) verify the email
  // themselves, but Firebase records Microsoft users as unverified — and
  // bouncing them to SSO loops forever (the IdP re-authenticates silently).
  // Custom-token sessions carry the provider in the claims; direct provider
  // sign-ins carry it in providerData.
  if (user.providerData.some((p) => p?.providerId && p.providerId !== 'password')) return true;
  try {
    const claims = (await user.getIdTokenResult()).claims as Record<string, unknown>;
    const provider = claims.provider as string | undefined;
    if (provider && provider !== 'password') return true;
  } catch { /* fall through to the reload check */ }
  try {
    await user.reload();
  } catch {
    return true; // transient — don't lock the user out
  }
  return user.emailVerified;
};

/**
 * One retry is armed per hand-off and disarmed the moment a session is
 * established (see the top of the guard). Native only in practice — on the web
 * `login()` never returns a user — and it bounds the native retry so a login
 * that resolves *without* a usable session (e.g. an unverified Microsoft
 * account) can't ping-pong the user through the auth sheet forever.
 */
let nativeLoginRetryArmed = true;

/**
 * Hand off to the centralized auth portal.
 *
 * Web (default `LocationAssignNavigator`): `login()` assigns `window.location`
 * and resolves with `undefined` while that navigation is in flight, so the
 * document is being torn down and `false` cancels a navigation nobody will see.
 * Unchanged from before this function existed.
 *
 * Native shell: the authorize leg runs in the system browser and `login()`
 * resolves IN PLACE with the signed-in user (auth-bridge v3) — no document load
 * ever happens. Returning `false` there would leave the user staring at whatever
 * was on screen, so we re-enter the same URL as a UrlTree: the guard runs again,
 * now finds a session, and lets it through. That is strictly better than the
 * alternative of forcing `location.reload()` to fake the web flow's page load —
 * a reload would drop in-app state and cost a full Angular bootstrap.
 *
 * Inside a Microsoft Teams tab this portal hand-off is a dead end — it's an
 * interactive redirect through Google/Microsoft and can't complete inside the
 * Teams iframe (third-party storage is partitioned there). Teams SSO (silent
 * AAD token -> exchangeTeamsToken -> signInWithCustomToken) is attempted
 * earlier in the guard; if it didn't produce a session, send the user to the
 * status page instead of the portal.
 */
const handOffToLogin = async (
  bridge: AuthBridge,
  router: Router,
  state: RouterStateSnapshot,
  inTeams: boolean,
): Promise<boolean | UrlTree> => {
  if (inTeams) {
    return router.parseUrl('/teams-no-access');
  }
  // Shells with in-app login (mobile): render our own login page instead of the
  // system-browser sheet. The page falls back to bridge.login() itself for the
  // providers the native sheets can't cover (Microsoft, SSO, signup).
  if (shellNativeSignIn()) {
    return router.createUrlTree(['/login/native'], { queryParams: { returnUrl: state.url } });
  }
  const user = await bridge.login();
  if (user && nativeLoginRetryArmed) {
    nativeLoginRetryArmed = false;
    return router.parseUrl(state.url);
  }
  return false;
};

export const angularFireAuthGuard: CanActivateFn = async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const firebase = inject(FirebaseService);
  const bridge = inject(AuthBridgeService).bridge;
  // Hoisted: inject() is only valid before the first await.
  const partnerDomains = inject(PartnerDomainsService);
  const hireIntent = inject(HireIntentService);
  const teams = inject(TeamsService);
  const redirect = inject(RedirectService);

  // Stash any `?hire=` before a login handoff can strip it: auth-bridge's
  // redirect_uri is origin + pathname, so the query string does not come back.
  // Captured here rather than per call site — the guard has four paths to
  // bridge.login() and all of them lose it.
  hireIntent.capture(next.queryParamMap.get('hire'));
  // Same loss for every other query param (deep links): remember the URL, and
  // after the code exchange below land back on it (utils/login-return-url).
  const isCallback = isLoginCallback(state.url);
  rememberReturnUrl(tabStorage(), state.url);

  // Must resolve before any hand-off decision below (including the catch
  // block's): a Teams session getting misrouted into the centralized-portal
  // redirect can never complete inside the Teams iframe (see handOffToLogin).
  const inTeams = await teams.ensureTeamsDetected().catch(() => false);

  try {
    // If returning from centralized auth with a ?code, exchange it for a session
    // before we read the auth state below.
    await bridge.ensureSession().catch(() => undefined);

    const authPipeFactory = next.data.authGuardPipe as AuthPipeGenerator || (() => loggedIn);
    let user = await retrieveUser(firebase);

    // Back from the portal with a session: re-navigate to the remembered URL
    // (minus code/state). The new navigation re-runs every check below.
    if (isCallback && loggedIn(user)) {
      return router.parseUrl(takeReturnUrl(tabStorage(), state.url));
    }
    if (loggedIn(user)) forgetReturnUrl(tabStorage());

    // Inside Teams, run the silent SSO exchange once per app load — even when
    // no local session exists yet — before falling back to the portal. On
    // failure teams.ssoStatus/ssoError are set for the /teams-no-access page,
    // which handOffToLogin routes to below.
    if (inTeams && !loggedIn(user) && teams.consumeAuthPending()) {
      try {
        await teams.performTeamsSso();
        user = await retrieveUser(firebase);
      } catch {
        // performTeamsSso already set ssoStatus/ssoError for the status page.
      }
    }

    // A real session means the last hand-off (if any) worked; re-arm the native
    // retry so a later sign-out → sign-in in the same document works too.
    if (loggedIn(user)) {
      nativeLoginRetryArmed = true;
    }

    // Skip email verification inside Teams — Entra already asserted the
    // identity, and the portal redirect can't complete in the iframe.
    if (!inTeams && user && !user.isAnonymous && !(await isEmailVerified(user))) {
      await firebase.auth.signOut().catch(() => {});
      return handOffToLogin(bridge, router, state, inTeams);
    }

    const can = authPipeFactory(next, state)(user);

    if (typeof can === 'boolean' && can && user) {
      // User is authenticated — check org membership.
      try {
        const userSnap = await firebase.globalFirestore.docGet(`users/${user.uid}`);
        // A pending local write (e.g. setOnlineStatus on app boot) can mask
        // the server fields in the snapshot view. Skip the redirect while
        // writes are in flight — the listener-driven user.service will fire
        // again once the server confirms and we'll re-check on next nav.
        if (userSnap?.metadata?.hasPendingWrites) {
          return true;
        }
        const userData = userSnap?.data();
        // Native shell: a brand-new account runs the in-app onboarding first. The portal does
        // this at /authorize, which the native login never visits; it also creates the
        // organization, so it replaces the web hand-off just below for those accounts.
        // Includes accounts with no organization: the web hand-off below cannot work in the
        // shell (see needsNativeOnboarding).
        if (shellNativeSignIn() && needsNativeOnboarding(userData) && !state.url.startsWith('/onboarding/native')) {
          return router.createUrlTree(['/onboarding/native']);
        }
        const orgIds: string[] = userData?.organizationIds || [];
        if (!orgIds.length) {
          // Partner-aware: a white-label user with no org creates it in the
          // PARTNER's console, not Crewdle's (2026-09-04). `originReady` waits
          // for the hostname resolution — this fires during boot, before the
          // org (and so the white-label org path) exists.
          // Same wait and destination as before, now under the shared redirect loader (it stays up
          // until the page unloads; Retry if it never does).
          const left = await redirect.leave({
            label: 'redirect.opening-setup',
            prepare: () => partnerDomains.originReady('admin', environment.adminConsoleUrl),
            go: (adminOrigin) => {
              window.location.href = `${adminOrigin}/new`;
            },
          });
          // A hand-off that could not start (the toast offers Try again) lets the person through,
          // as the old `catch {}` did, instead of cancelling the navigation onto a blank route.
          if (left) return false;
        }
      } catch {
        // If we can't read the user doc, allow through — don't block on transient errors
      }
      // Strip the OAuth callback params from the URL. The bridge clears them via
      // history.replaceState, but Angular Router re-applies this navigation's
      // query params when it finalizes — so redirect to the clean path instead.
      if (next.queryParamMap.has('code')) {
        return router.parseUrl(state.url.split('?')[0]);
      }
      return true;
    }

    if (typeof can === 'boolean') {
      // A redirectLoggedInTo route (e.g. /login, /signup) with no real session
      // returns `true` here — hand off to centralized auth instead of rendering
      // the in-app login page.
      if (can && !loggedIn(user)) {
        return handOffToLogin(bridge, router, state, inTeams);
      }
      return can;
    } else if (Array.isArray(can)) {
      return router.createUrlTree(can);
    } else {
      // A redirect target string. When the user isn't authenticated, hand off
      // to centralized auth instead of the local /login; otherwise honor the
      // in-app redirect (e.g. a logged-in user bounced off /login → /home).
      if (!user || user.isAnonymous) {
        return handOffToLogin(bridge, router, state, inTeams);
      }
      return router.parseUrl(can);
    }
  } catch {
    return handOffToLogin(bridge, router, state, inTeams);
  }
};

export const redirectUnauthorizedTo: (redirect: string|any[]) => any =
  (redirect) => (user: User) => loggedIn(user) || redirect;
export const redirectLoggedInTo: (redirect: string|any[]) => any =
  (redirect) => (user: User) => loggedIn(user) && redirect || true;
